import * as React from 'react';
import {RequireAtLeastOne} from 'type-fest';
import {
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useTransitionStyles,
} from '@floating-ui/react';
import type {FloatingFocusManagerProps} from '@floating-ui/react';

import {useFloatingBase, useAnchorRefPosition} from '../../hooks';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';
import {isInJest} from '../../utils';

import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import type {UseFloatingBaseProps, AnchorRef} from '../../hooks';
import type {MaskPropsType} from '../Mask/Mask';

export type PopoverProps = {
  children: React.ReactNode;
  /**
   * Space between the Popover and its container.
   */
  containerPadding?: UseFloatingBaseProps['containerPadding'];
  /**
   * The initial open state.
   */
  defaultIsOpen?: boolean;
  /**
   * Number of pixels to shift the Popover horizontally.
   */
  horizontalOffset?: number;
  /**
   * How to apply limitShift() to the shift middleware, preventing detachment.
   *
   * Setting this to false can keep a popover in the UI in certain complex circumstances
   * (e.g. comment popovers in an absolutely positioned element as it translates)
   *
   * Defaults to `true`.
   */
  limitShiftOptions?: UseFloatingBaseProps['limitShiftOptions'];
  /**
   * Props related to rendering the Mask component.
   */
  maskProps?: RequireAtLeastOne<MaskPropsType>;
  /**
   * Max-height of the Popover's content. Unset by default.
   */
  maxHeight?: React.CSSProperties['maxHeight'];
  /**
   * Max-width of the Popover. Defaults to 280px.
   */
  maxWidth?: UseFloatingBaseProps['maxWidth'];
  /**
   * Min-height of the Popover. Unset by default.
   */
  minHeight?: React.CSSProperties['minHeight'];
  /**
   * Min-width of the Popover.
   */
  minWidth?: UseFloatingBaseProps['minWidth'];
  /**
   * Whether the PopoverContent should resize to match the width of the PopoverTrigger.
   */
  shouldResizeContentToMatchTrigger?: boolean;
  /**
   * Sets both min-width and max-width of the Popover.
   */
  width?: UseFloatingBaseProps['minWidth'];
  /**
   * Controlled open state.
   */
  isOpen?: UseFloatingBaseProps['isOpen'];
  /**
   * Callback when an open or close action occurs. Necessary for controlled open/close.
   *
   * 1. Click on PopoverTrigger (when popover is closed)
   */
  onOpenChange?: UseFloatingBaseProps['onOpenChange'];
  /**
   * Placement of the Popover.
   */
  placement?: UseFloatingBaseProps['placement'];
  /**
   * Determines if focus should return to trigger element. Also accepts a `ref` to explicitly set the return focus element.
   */
  returnFocus?: FloatingFocusManagerProps['returnFocus'];
  /**
   * Whether the Popover should escape its viewport and render in the window.
   */
  shouldEscapeViewport?: UseFloatingBaseProps['shouldEscapeViewport'];
  /**
   * Closes the popover when clicking outside of it.
   */
  shouldHideOnClickOutside?: boolean;
  /**
   * Prevents scrolling of the main page body when the popover is opened.
   */
  shouldPreventBodyScroll?: boolean;
  /**
   * Whether the Mask should render.
   */
  shouldShowMask?: boolean;
  /**
   * Whether the Popover should trap focus within itself.
   */
  shouldTrapFocus?: boolean;
  /**
   * Number of pixels to shift the Popover vertically
   */
  verticalOffset?: number;
  /**
   * The reference to the anchor element if it is different than the PopoverTrigger.
   * @NOTE: You must use React.useState (not plain `refs`) to hold your ref to ensure that it is reactive.
   */
  anchorRef?: AnchorRef;
} & HTMLAttributes &
  SpringTooling;

type PopoverContextType = ReturnType<typeof usePopover> & {
  arrowRef: UsePopoverProps['arrowRef'];
  colorVariant?: PopoverContentColorVariant;
  maskProps?: PopoverProps['maskProps'];
  maxHeight?: PopoverProps['maxHeight'];
  minHeight?: PopoverProps['minHeight'];
  shouldShowMask: PopoverProps['shouldShowMask'];
  returnFocus: PopoverProps['returnFocus'];
  shouldTrapFocus: PopoverProps['shouldTrapFocus'];
};

export const PopoverContext = React.createContext<PopoverContextType | null>(
  null
);

export const usePopoverContext = (): PopoverContextType => {
  const context = React.useContext(PopoverContext);
  const isTestEnvironment = process.env.NODE_ENV === 'test';

  if (!context && !isTestEnvironment) {
    throw new Error('You must use component within Popover Context');
  }

  /**
   * Context should only be null in a test environment.
   * We're okay with this because we want to be able to test Popover's subcomponents in isolation.
   */
  // @ts-expect-error TS2322 "null is not assignable to type PopoverContextType"
  return context;
};

export type PopoverContentColorVariant =
  | 'default'
  | 'secondary'
  | 'tertiary'
  | 'blue';

type UsePopoverProps = UseFloatingBaseProps & {
  anchorRef?: AnchorRef;
  defaultIsOpen?: boolean;
  horizontalOffset?: number;
  shouldHideOnClickOutside?: boolean;
  shouldLimitShift?: boolean;
  shouldPreventBodyScroll?: boolean;
  shouldResizeContentToMatchTrigger?: boolean;
  verticalOffset?: number;
};

export const HOVERABLE_TOOLTIP_DELAY = {
  open: 450,
  close: 250,
};

export const usePopover = ({
  anchorRef,
  arrowRef,
  containerPadding,
  defaultIsOpen,
  horizontalOffset,
  isOpen: controlledIsOpen,
  limitShiftOptions,
  maxWidth = 280,
  minWidth = 'min-content',
  onOpenChange: onOpenChangeProp,
  placement,
  shouldEscapeViewport,
  shouldHideOnClickOutside = true,
  shouldPreventBodyScroll,
  shouldResizeContentToMatchTrigger,
  verticalOffset,
}: UsePopoverProps) => {
  const offsetModifier: UseFloatingBaseProps['offsetModifier'] =
    React.useCallback(() => {
      if (horizontalOffset === undefined && verticalOffset === undefined) {
        return null;
      }

      const offsetModifiers = {
        horizontalOffset,
        verticalOffset,
      };

      return offsetModifiers;
    }, [horizontalOffset, verticalOffset]);

  const {onOutsidePress, openLegacyOverlays, onOpenChange, ...data} =
    useFloatingBase({
      arrowRef,
      containerPadding,
      defaultIsOpen,
      isOpen: controlledIsOpen,
      limitShiftOptions,
      maxWidth,
      minWidth,
      offsetModifier,
      onOpenChange: onOpenChangeProp,
      placement,
      shouldEscapeViewport,
      shouldMatchTriggerWidth: shouldResizeContentToMatchTrigger,
    });

  const {context} = data;

  const click = useClick(context, {
    enabled: controlledIsOpen == null,
  });
  const dismiss = useDismiss(context, {
    escapeKey: openLegacyOverlays.length === 0,
    outsidePress: shouldHideOnClickOutside ? onOutsidePress : false,
    bubbles: false,
  });

  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  useAnchorRefPosition({anchorRef, context});

  return React.useMemo(
    () => ({
      ...interactions,
      ...data,
      onOpenChange,
      shouldPreventBodyScroll,
    }),
    [data, interactions, onOpenChange, shouldPreventBodyScroll]
  );
};

// How far the Popover should move when appearing.
const SLIDE_DISTANCE = '4px';
export const POPOVER_DURATION = isInJest ? 0 : 160;

export const usePopoverTransition = ({
  shouldAnimate,
}: {
  shouldAnimate?: boolean;
}) => {
  const reduceMotion = usePrefersReducedMotion();
  const {context, getFloatingProps} = usePopoverContext();

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {
    duration: {
      // Putting these here for now. We may wish to move these to useFloatingBase as a CONST.
      open: POPOVER_DURATION,
      close: shouldAnimate ? POPOVER_DURATION : 0,
    },
    initial: ({side}) => {
      const initialStyles = {
        opacity: 0,
      };

      if (reduceMotion) {
        return initialStyles;
      }

      switch (side) {
        case 'top': {
          return {
            ...initialStyles,
            transform: `translateY(${SLIDE_DISTANCE})`,
          };
        }
        case 'bottom': {
          return {
            ...initialStyles,
            transform: `translateY(-${SLIDE_DISTANCE})`,
          };
        }
        case 'left': {
          return {
            ...initialStyles,
            transform: `translateX(${SLIDE_DISTANCE})`,
          };
        }
        case 'right': {
          return {
            ...initialStyles,
            transform: `translateX(-${SLIDE_DISTANCE})`,
          };
        }
        default: {
          return initialStyles;
        }
      }
    },
  });

  const {x, y, refs, strategy} = context;
  const {setFloating} = refs;

  return React.useMemo(
    () => ({
      x,
      y,
      setFloating,
      strategy,
      getFloatingProps,
      isMounted,
      transitionStyles,
      context,
    }),
    [
      isMounted,
      transitionStyles,
      x,
      y,
      setFloating,
      strategy,
      getFloatingProps,
      context,
    ]
  );
};
